import type { ComponentType, JSX, PropsWithChildren } from 'react';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PolicyPageCombinedState, PolicyPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PolicyPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<PolicyPageCombinedState> };

type Props = {
	slug: string | undefined;
	prefetchedData: PolicyPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function PolicyPageContextProvider({
	slug,
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return <PolicyPageDataContextProvider pageData={pageData}>{children}</PolicyPageDataContextProvider>;
}
